import { FORMAT, SCHEMA_VERSION, MAX_PRODUCTS } from './productTransfer/schema';
import { extractProductDependencies } from './productTransfer/dependencies';
import { validateProductTransfer } from './productTransfer/validation';

// Pure: never imports Firebase, queries the network, or mutates the catalog.
export function buildProductExport({ products, selectedIds, sourceName = 'ValenPOS', exportId, exportedAt, catalogComplete }) {
  const errors = [], results = new Map(), catalog = new Map();
  if (!catalogComplete) errors.push('El catálogo completo debe estar cargado antes de exportar');
  products.forEach((p) => {
    if (!p?.id) return;
    if (catalog.has(p.id)) errors.push(`ID duplicado en el catálogo: ${p.id}`);
    catalog.set(p.id, p);
  });
  const roots = [...new Set(selectedIds)];
  const queue = [...roots], visited = new Set();
  for (let i = 0; i < queue.length; i += 1) {
    const id = queue[i];
    if (visited.has(id)) continue;
    visited.add(id);
    if (visited.size > MAX_PRODUCTS) { errors.push(`La selección supera ${MAX_PRODUCTS} productos con dependencias`); break; }
    const product = catalog.get(id);
    if (!product || product.deleted === true) { errors.push(`Producto o dependencia inexistente/eliminada: ${product?.name || id}`); continue; }
    const result = extractProductDependencies(product, catalog);
    results.set(id, result);
    result.errors.forEach((message) => errors.push(`${product.name || id}: ${message}`));
    result.references.forEach((ref) => {
      if (!catalog.has(ref) || catalog.get(ref).deleted === true) errors.push(`${product.name || id}: falta la dependencia activa ${catalog.get(ref)?.name || ref}`);
      queue.push(ref);
    });
  }
  const rootSet = new Set(roots);
  const dependencies = [...results.keys()].filter((id) => !rootSet.has(id)).sort();
  const refs = new Map([...roots.map((id, i) => [id, `p${i + 1}`]), ...dependencies.map((id, i) => [id, `d${i + 1}`])]);
  const selectors = [], selectorKeys = new Map();
  const nodes = new Map();
  results.forEach((result, id) => {
    const data = result.data; // Newly constructed DTO, not the source product.
    const config = data.config;
    const localSelectors = result.selectors.map((selector) => {
      const key = JSON.stringify(selector);
      if (!selectorKeys.has(key)) {
        const ref = `selector${selectors.length + 1}`;
        selectorKeys.set(key, ref);
        selectors.push({ ref, kind: selector.kind, sourceCategory: selector.sourceCategory, unit: selector.unit, memberRefs: selector.memberRefs.map((member) => refs.get(member) || '') });
      }
      return selectorKeys.get(key);
    });
    if (config) {
      [...(config.recipe?.baseItems || []), ...(config.extraItems || [])].forEach((item) => { item.productRef = refs.get(item.productRef) || ''; });
      (config.recipe?.choiceGroups || []).forEach((group) => {
        group.selectorRef = localSelectors[group.selectorRef];
        if (group.defaultProductRef) group.defaultProductRef = refs.get(group.defaultProductRef) || '';
      });
      if (config.baseInsumoRef) config.baseInsumoRef = refs.get(config.baseInsumoRef) || '';
      if (config.promotion) config.promotion.sourceProductRef = refs.get(config.promotion.sourceProductRef) || '';
    }
    nodes.set(id, { ref: refs.get(id), sourceProductId: id, data, warnings: result.warnings });
  });
  const file = { format: FORMAT, schemaVersion: SCHEMA_VERSION, exportId, exportedAt, source: { name: sourceName || 'ValenPOS' }, products: roots.map((id) => nodes.get(id)).filter(Boolean), dependencies: { products: dependencies.map((id) => nodes.get(id)), selectors } };
  errors.push(...validateProductTransfer(file).errors);
  const warnings = [...nodes.values()].flatMap((node) => node.warnings.map((warning) => ({ product: node.data.name, ...warning })));
  return { file, errors: [...new Set(errors)], warnings, status: errors.length ? 'BLOCKED' : warnings.length ? 'WARNING' : 'SUPPORTED' };
}

export function serializeProductExport(review) {
  if (review.errors.length || review.status === 'BLOCKED') throw new Error('La exportación contiene bloqueos. Revisa la selección.');
  const validation = validateProductTransfer(review.file);
  if (!validation.valid) throw new Error(validation.errors.join('\n'));
  const json = JSON.stringify(review.file, null, 2);
  const roundTrip = validateProductTransfer(JSON.parse(json));
  if (!roundTrip.valid) throw new Error(roundTrip.errors.join('\n'));
  return json;
}

export function slugifyExportSourceName(value) {
  const slug = String(value || 'ValenPOS')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'valenpos';
}

export function buildProductExportFilename(review) {
  const source = slugifyExportSourceName(review?.file?.source?.name);
  const date = new Date(review?.file?.exportedAt);
  if (Number.isNaN(date.getTime())) return `valenpos-${source}.json`;
  const pad = (value) => String(value).padStart(2, '0');
  const stamp = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}-${pad(date.getHours())}${pad(date.getMinutes())}`;
  return `valenpos-${source}-${stamp}.json`;
}

export function downloadProductExport(review) {
  const json = serializeProductExport(review);
  const url = URL.createObjectURL(new Blob([json], { type: 'application/json;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = buildProductExportFilename(review);
  document.body.appendChild(anchor);
  try { anchor.click(); } finally { anchor.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000); }
}
